import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Betriebskontakt, isVvvoPlaceholderBezeichnung } from './betriebskontakt';
import { BetriebskontakteService } from './betriebskontakte.service';
import { MessageService } from '../../../services/message-service/message.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

export interface BearbeitenDialogData {
    kontakt: Betriebskontakt;
}

@Component({
    selector: 'kiene-betriebskontakte-bearbeiten-dialog',
    templateUrl: './betriebskontakte-bearbeiten-dialog.component.html',
    styleUrls: ['./betriebskontakte-bearbeiten-dialog.component.scss'],
})
export class BetriebskontakteBearbeitenDialogComponent implements OnInit {
    bearbeitenForm: FormGroup;
    ladeSpeichern = false;
    isReadOnlyKontakt = false;
    /** Pass-Scan-Platzhalter „VVVO “ + 15 Ziffern – Name/Adresse dürfen korrigiert werden. */
    isVvvoPlaceholderBetrieb = false;
    private readonly editableBetriebsdatenControls = [
        'bezeichnung',
        'zusatz',
        'strasse',
        'plz',
        'ort',
        'ortsteil',
        'email',
        'telefon_1',
        'telefon_2',
        'mobil_1',
        'mobil_2',
    ];

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<BetriebskontakteBearbeitenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: BearbeitenDialogData,
        private betriebskontakteService: BetriebskontakteService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
    ) {
        this.bearbeitenForm = this.fb.group({
            favorit: [false],
            ist_kunde: [false],
            // Betriebsdaten
            bezeichnung: [''],
            zusatz: [''],
            strasse: [''],
            plz: [''],
            ort: [''],
            ortsteil: [''],
            email: [''],
            telefon_1: [''],
            telefon_2: [''],
            mobil_1: [''],
            mobil_2: [''],
        });
    }

    ngOnInit(): void {
        const currentUserId = Number(this.localStorageService.getCurrentUser()?.benutzer_id ?? 0);
        const kontaktCreatorId = Number(this.data?.kontakt?.erstellt_von ?? 0);
        const kontaktUserId = Number(this.data?.kontakt?.benutzer_id ?? 0);
        this.isReadOnlyKontakt = !(
            currentUserId > 0 &&
            kontaktCreatorId > 0 &&
            kontaktUserId > 0 &&
            kontaktCreatorId === currentUserId &&
            kontaktUserId === currentUserId
        );
        this.isVvvoPlaceholderBetrieb = isVvvoPlaceholderBezeichnung(this.data?.kontakt?.bezeichnung);

        if (this.data.kontakt) {
            this.bearbeitenForm.patchValue({
                favorit: Number(this.data.kontakt.favorit) > 0,
                ist_kunde: Number(this.data.kontakt.ist_kunde) > 0,
                // Betriebsdaten
                bezeichnung: this.data.kontakt.bezeichnung || '',
                zusatz: this.data.kontakt.zusatz || '',
                strasse: this.data.kontakt.strasse || '',
                plz: this.data.kontakt.plz || '',
                ort: this.data.kontakt.ort || '',
                ortsteil: this.data.kontakt.ortsteil || '',
                email: this.data.kontakt.email || '',
                telefon_1: this.data.kontakt.telefon_1 || '',
                telefon_2: this.data.kontakt.telefon_2 || '',
                mobil_1: this.data.kontakt.mobil_1 || '',
                mobil_2: this.data.kontakt.mobil_2 || '',
            });
        }

        // Deaktiviere ist_kunde FormControl, da es automatisch vom Backend gesetzt wird
        this.bearbeitenForm.get('ist_kunde')?.disable();

        if (this.isReadOnlyKontakt) {
            this.bearbeitenForm.disable();
            return;
        }

        // Überwache Änderungen an ist_kunde Checkbox für Betriebsdaten-Sichtbarkeit
        this.bearbeitenForm.get('ist_kunde')?.valueChanges.subscribe(istKunde => {
            this.applyBetriebsdatenEditability(!!istKunde);
        });

        this.applyBetriebsdatenEditability(!!this.bearbeitenForm.get('ist_kunde')?.value);
    }

    get canEditBetriebsdaten(): boolean {
        return this.isVvvoPlaceholderBetrieb || !this.bearbeitenForm.get('ist_kunde')?.value;
    }

    async speichern(): Promise<void> {
        if (this.isReadOnlyKontakt) {
            return;
        }
        if (this.bearbeitenForm.valid) {
            this.ladeSpeichern = true;

            try {
                const updateData: Betriebskontakt = {
                    ...this.data.kontakt,
                    favorit: this.bearbeitenForm.get('favorit')?.value ? 1 : 0,
                    ist_kunde: this.bearbeitenForm.get('ist_kunde')?.value ? 1 : 0,
                };

                if (this.canEditBetriebsdaten) {
                    updateData.bezeichnung = this.bearbeitenForm.get('bezeichnung')?.value;
                    updateData.zusatz = this.bearbeitenForm.get('zusatz')?.value;
                    updateData.strasse = this.bearbeitenForm.get('strasse')?.value;
                    updateData.plz = this.bearbeitenForm.get('plz')?.value;
                    updateData.ort = this.bearbeitenForm.get('ort')?.value;
                    updateData.ortsteil = this.bearbeitenForm.get('ortsteil')?.value;
                    updateData.email = this.bearbeitenForm.get('email')?.value;
                    updateData.telefon_1 = this.bearbeitenForm.get('telefon_1')?.value;
                    updateData.telefon_2 = this.bearbeitenForm.get('telefon_2')?.value;
                    updateData.mobil_1 = this.bearbeitenForm.get('mobil_1')?.value;
                    updateData.mobil_2 = this.bearbeitenForm.get('mobil_2')?.value;
                }

                await this.betriebskontakteService.aktualisiereBetriebskontakt(updateData).toPromise();

                this.messageService.infoMessage('Kontakt erfolgreich aktualisiert');
                this.dialogRef.close(true);
            } catch (error) {
                this.messageService.errorMessage('Fehler beim Aktualisieren des Kontakts', error);
            } finally {
                this.ladeSpeichern = false;
            }
        }
    }

    abbrechen(): void {
        this.dialogRef.close(false);
    }

    private applyBetriebsdatenEditability(istKunde: boolean): void {
        const disable = istKunde && !this.isVvvoPlaceholderBetrieb;
        this.toggleBetriebsdatenControls(disable);
    }

    private toggleBetriebsdatenControls(disable: boolean): void {
        this.editableBetriebsdatenControls.forEach(controlName => {
            const ctrl = this.bearbeitenForm.get(controlName);
            if (disable) {
                ctrl?.disable();
            } else {
                ctrl?.enable();
            }
        });
    }
}
